import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { MediaItem } from "@/mocks/media";

interface HeroSliderProps {
  items: MediaItem[];
}

export default function HeroSlider({ items }: HeroSliderProps) {
  const [index, setIndex] = useState(0);
  const count = items.length;

  const next = useCallback(() => {
    setIndex((i) => (i + 1) % count);
  }, [count]);

  const prev = () => setIndex((i) => (i - 1 + count) % count);

  useEffect(() => {
    const timer = setInterval(next, 6000);
    return () => clearInterval(timer);
  }, [next]);

  if (count === 0) return null;
  const active = items[index];

  return (
    <section className="relative w-full overflow-hidden bg-background-950">
      <div className="relative h-[460px] w-full md:h-[600px]">
        {items.map((item, i) => (
          <div
            key={item.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              i === index ? "opacity-100" : "opacity-0"
            }`}
          >
            <img
              src={item.cover}
              alt={`${item.title} 焦点海报`}
              title={`${item.title} - ${item.category}`}
              className={`h-full w-full object-cover object-top ${
                i === index ? "animate-slow-zoom" : ""
              }`}
            />
          </div>
        ))}

        <div className="absolute inset-0 bg-gradient-to-r from-background-950 via-background-950/80 to-background-950/20"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-transparent to-background-950/40"></div>

        <div className="absolute inset-0 flex items-center">
          <div className="mx-auto w-full max-w-[1280px] px-4 md:px-6">
            <div key={active.id} className="max-w-xl animate-fade-up">
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <span className="rounded-full bg-accent-500 px-3 py-1 font-medium text-background-50">
                  焦点推荐
                </span>
                <span className="rounded-full border border-background-700/60 px-3 py-1 text-foreground-800">
                  {active.category}
                </span>
                <span className="flex items-center gap-1 font-medium text-accent-400">
                  <i className="ri-star-fill text-sm"></i>
                  {active.rating} 分
                </span>
              </div>

              <h2 className="mt-4 font-heading text-3xl font-bold tracking-wide text-foreground-950 md:text-5xl">
                {active.title}
              </h2>

              <p className="mt-3 line-clamp-2 text-sm leading-relaxed text-foreground-700 md:text-base">
                {active.synopsis}
              </p>

              <div className="mt-5 flex flex-wrap items-center gap-3 text-sm text-foreground-600">
                <span>导演：{active.director}</span>
                <span className="text-background-700">|</span>
                <span>主演：{active.actors.slice(0, 3).join(" / ")}</span>
                <span className="text-background-700">|</span>
                <span>{active.year} · {active.region}</span>
              </div>

              <div className="mt-7 flex flex-wrap items-center gap-3">
                <Link
                  to={`/detail/${active.id}`}
                  className="flex h-11 items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
                >
                  <i className="ri-play-fill text-lg"></i>
                  立即查看
                </Link>
                <Link
                  to={`/category/${encodeURIComponent(active.category)}`}
                  className="flex h-11 items-center gap-2 whitespace-nowrap rounded-md border border-background-600/70 px-6 text-sm font-medium text-foreground-900 transition-colors hover:border-accent-500/60 hover:text-accent-400"
                >
                  <i className="ri-list-check-2 text-base"></i>
                  更多{active.category}
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-6 left-1/2 flex -translate-x-1/2 items-center gap-2">
          {items.map((item, i) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setIndex(i)}
              aria-label={`切换到 ${item.title}`}
              className={`h-2 rounded-full transition-all ${
                i === index ? "w-8 bg-accent-500" : "w-2 bg-background-500"
              }`}
            ></button>
          ))}
        </div>

        <button
          type="button"
          onClick={prev}
          aria-label="上一张"
          className="absolute left-3 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-background-600/60 bg-background-950/50 text-foreground-900 backdrop-blur transition-colors hover:border-accent-500/60 hover:text-accent-400 md:flex"
        >
          <i className="ri-arrow-left-s-line text-2xl"></i>
        </button>
        <button
          type="button"
          onClick={next}
          aria-label="下一张"
          className="absolute right-3 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-background-600/60 bg-background-950/50 text-foreground-900 backdrop-blur transition-colors hover:border-accent-500/60 hover:text-accent-400 md:flex"
        >
          <i className="ri-arrow-right-s-line text-2xl"></i>
        </button>
      </div>
    </section>
  );
}